import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { 
  CreditCard,
  User, 
  Mail, 
  Phone, 
  Building, 
  Calendar, 
  DollarSign, 
  Clock,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Briefcase,
  MessageSquare,
  TrendingUp
} from 'lucide-react';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';

const AdminConsultationRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, completed, failed
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [sendingMeeting, setSendingMeeting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=getConsultationRequests`);
      const result = await response.json();
      
      if (result.status === 'success') {
        setRequests(result.data || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching consultation requests:', error);
      setLoading(false);
    }
  };

  const sendMeetingInvitation = async () => {
    if (!meetingDate || !meetingTime) {
      alert('Lütfen tarih ve saat seçin');
      return;
    }

    setSendingMeeting(true);
    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=sendMeetingInvitation`, {
        method: 'POST',
        body: JSON.stringify({
          consultation_id: selectedRequest.id,
          customer_name: selectedRequest.full_name,
          customer_email: selectedRequest.email,
          customer_phone: selectedRequest.phone,
          company_name: selectedRequest.company_name,
          meeting_date: meetingDate,
          meeting_time: meetingTime
        })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        alert('✅ Toplantı daveti başarıyla gönderildi!');
        setMeetingDate('');
        setMeetingTime('');
      } else {
        alert('❌ Hata: ' + (result.message || 'Gönderim başarısız'));
      }
    } catch (error) {
      console.error('Error sending meeting invitation:', error);
      alert('❌ Toplantı daveti gönderilemedi');
    } finally {
      setSendingMeeting(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        color: 'bg-yellow-500', 
        text: 'Beklemede',
        icon: Clock 
      },
      completed: { 
        color: 'bg-green-500', 
        text: 'Tamamlandı',
        icon: CheckCircle 
      },
      failed: { 
        color: 'bg-red-500', 
        text: 'Başarısız',
        icon: XCircle 
      },
      refunded: { 
        color: 'bg-gray-500', 
        text: 'İade Edildi',
        icon: AlertCircle 
      }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} text-white flex items-center space-x-1`}>
        <Icon className="w-3 h-3" />
        <span>{config.text}</span>
      </Badge>
    );
  };

  const getServiceTypeName = (serviceType) => {
    const serviceNames = {
      'ecommerce': 'E-ticaret Sitesi',
      'eexport': 'E-ihracat Çözümleri',
      'hosting': 'Hosting & Domain',
      'ai': 'Yapay Zeka Çözümleri',
      'marketing': 'Dijital Pazarlama',
      'design': 'Tasarım Hizmetleri',
      'other': 'Diğer'
    };
    return serviceNames[serviceType] || serviceType;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(request => {
    if (filter === 'all') return true;
    return request.payment_status === filter;
  });

  // İstatistikler
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.payment_status === 'pending').length,
    completed: requests.filter(r => r.payment_status === 'completed').length,
    failed: requests.filter(r => r.payment_status === 'failed').length,
    totalRevenue: requests
      .filter(r => r.payment_status === 'completed')
      .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Danışmanlık talepleri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Ücretli Danışmanlık Talepleri</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
            Müşterilerden gelen ücretli danışmanlık taleplerini yönetin
          </p>
        </div>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 truncate">Toplam Talep</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">{stats.total}</p>
              </div>
              <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-blue-500 flex-shrink-0 ml-2" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 truncate">Bekleyen</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-yellow-600 truncate">{stats.pending}</p>
              </div>
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-yellow-500 flex-shrink-0 ml-2" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 truncate">Tamamlandı</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600 truncate">{stats.completed}</p>
              </div>
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-green-500 flex-shrink-0 ml-2" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 truncate">Başarısız</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600 truncate">{stats.failed}</p>
              </div>
              <XCircle className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-red-500 flex-shrink-0 ml-2" />
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-2 sm:col-span-1">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 truncate">Toplam Gelir</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-600 truncate">
                  ₺{stats.totalRevenue.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-purple-500 flex-shrink-0 ml-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtreler */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setFilter('all')}
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          className="text-xs sm:text-sm"
        >
          Tümü ({stats.total})
        </Button>
        <Button
          onClick={() => setFilter('pending')}
          variant={filter === 'pending' ? 'default' : 'outline'}
          size="sm"
          className={`text-xs sm:text-sm ${filter === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
        >
          Bekleyen ({stats.pending})
        </Button>
        <Button
          onClick={() => setFilter('completed')}
          variant={filter === 'completed' ? 'default' : 'outline'}
          size="sm"
          className={`text-xs sm:text-sm ${filter === 'completed' ? 'bg-green-500 hover:bg-green-600' : ''}`}
        >
          Tamamlandı ({stats.completed})
        </Button>
        <Button
          onClick={() => setFilter('failed')}
          variant={filter === 'failed' ? 'default' : 'outline'}
          size="sm"
          className={`text-xs sm:text-sm ${filter === 'failed' ? 'bg-red-500 hover:bg-red-600' : ''}`}
        >
          Başarısız ({stats.failed})
        </Button>
      </div>

      {/* Talepler Listesi */}
      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="p-4 sm:p-8 text-center">
            <CreditCard className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-xl font-semibold text-gray-900 mb-2">
              {filter === 'all' ? 'Henüz talep yok' : 'Bu filtrede talep yok'}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">
              {filter === 'all' 
                ? 'Müşterilerden ücretli danışmanlık talepleri geldiğinde burada görünecek.'
                : 'Bu durumda talep bulunamadı.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:gap-6">
          {filteredRequests.map((request) => (
            <Card key={request.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 truncate">
                        {request.full_name}
                      </h3>
                      {getPaymentStatusBadge(request.payment_status)}
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] sm:text-xs">
                        ₺{parseFloat(request.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </Badge>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500">
                      Sipariş No: <span className="font-mono font-medium">{request.order_id}</span>
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDetails(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto text-xs sm:text-sm"
                  >
                    <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Detaylar
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="flex items-center space-x-2 min-w-0">
                    <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-600 truncate break-all">{request.email}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 min-w-0">
                    <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-600 truncate">{request.phone}</span>
                  </div>
                  
                  {request.company_name && (
                    <div className="flex items-center space-x-2 min-w-0">
                      <Building className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-600 truncate">{request.company_name}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2 min-w-0">
                    <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-600 truncate">
                      {getServiceTypeName(request.service_type)}
                    </span>
                  </div>
                </div>

                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 text-xs sm:text-sm text-gray-500">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">Talep: {formatDate(request.created_at)}</span>
                    </div>
                    {request.payment_date && (
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                        <span className="truncate">Ödeme: {formatDate(request.payment_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detay Modal */}
      {showDetails && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-2 sm:p-4">
          <Card className="max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                <CardTitle className="text-base sm:text-lg lg:text-xl">Danışmanlık Talebi Detayları</CardTitle>
                <Button
                  onClick={() => setShowDetails(false)}
                  variant="ghost"
                  className="text-white hover:bg-white/20 self-end sm:self-auto"
                  size="sm"
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
              {/* Müşteri Bilgileri */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Müşteri Bilgileri
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Ad Soyad</p>
                    <p className="font-medium text-xs sm:text-sm truncate">{selectedRequest.full_name}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">E-posta</p>
                    <p className="font-medium text-xs sm:text-sm truncate break-all">{selectedRequest.email}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Telefon</p>
                    <p className="font-medium text-xs sm:text-sm truncate">{selectedRequest.phone}</p>
                  </div>
                  {selectedRequest.company_name && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Şirket</p>
                      <p className="font-medium text-xs sm:text-sm truncate">{selectedRequest.company_name}</p>
                    </div>
                  )}
                  {selectedRequest.industry && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Sektör</p>
                      <p className="font-medium text-xs sm:text-sm truncate">{selectedRequest.industry}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Proje Bilgileri */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                  <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Proje Bilgileri
                </h3>
                <div className="space-y-3 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Hizmet Türü</p>
                    <p className="font-medium text-xs sm:text-sm truncate">{getServiceTypeName(selectedRequest.service_type)}</p>
                  </div>
                  {selectedRequest.budget_range && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Bütçe Aralığı</p>
                      <p className="font-medium text-xs sm:text-sm truncate">{selectedRequest.budget_range}</p>
                    </div>
                  )}
                  {selectedRequest.timeline && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Süre</p>
                      <p className="font-medium text-xs sm:text-sm truncate">{selectedRequest.timeline}</p>
                    </div>
                  )}
                  {selectedRequest.project_description && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Proje Açıklaması</p>
                      <p className="font-medium text-xs sm:text-sm whitespace-pre-wrap break-words">{selectedRequest.project_description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Ödeme Bilgileri */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Ödeme Bilgileri
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Sipariş No</p>
                    <p className="font-mono font-medium text-xs sm:text-sm truncate">{selectedRequest.order_id}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">İşlem No (PayTR)</p>
                    <p className="font-mono font-medium text-xs truncate break-all">
                      {selectedRequest.transaction_id || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Tutar</p>
                    <p className="font-medium text-purple-600 text-sm sm:text-lg">
                      ₺{parseFloat(selectedRequest.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Ödeme Durumu</p>
                    <div className="mt-1">{getPaymentStatusBadge(selectedRequest.payment_status)}</div>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Ödeme Tarihi</p>
                    <p className="font-medium text-xs sm:text-sm truncate">{formatDate(selectedRequest.payment_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Talep Tarihi</p>
                    <p className="font-medium text-xs sm:text-sm truncate">{formatDate(selectedRequest.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Son Güncelleme</p>
                    <p className="font-medium text-xs sm:text-sm truncate">{formatDate(selectedRequest.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Toplantı Randevusu Gönder */}
              <div className="border-t pt-4 sm:pt-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600" />
                  Toplantı Randevusu Gönder
                </h3>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 sm:p-4 lg:p-6 rounded-lg border border-blue-200">
                  <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4">
                    Müşteriye toplantı daveti gönderin. E-posta şablonu kullanılarak müşteri bilgileriyle birlikte gönderilecektir.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Toplantı Tarihi *
                      </label>
                      <input
                        type="date"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Toplantı Saati *
                      </label>
                      <input
                        type="time"
                        value={meetingTime}
                        onChange={(e) => setMeetingTime(e.target.value)}
                        className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={sendMeetingInvitation}
                    disabled={sendingMeeting || !meetingDate || !meetingTime}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm"
                  >
                    {sendingMeeting ? (
                      <>
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Toplantı Daveti Gönder
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminConsultationRequests;





